import React from 'react';
import { theme } from '../../styles/theme';
import Badge from './Badge';
import { formatFileSize } from '../../utils/bulkUpload';

export type FileStatus =
  // Processing driven by this tab (single-file flow)
  | 'uploaded'
  | 'processing'
  | 'completed'
  | 'error'
  // Bulk upload, per file
  | 'upload_pending'
  | 'uploading'
  // Processing queued on the server (status read back from the server)
  | 'queued'
  | 'ready'
  | 'needs_attention'
  | 'failed'
  | 'waiting_capacity'
  | 'deleted'
  // Selected but never acknowledged by the server (page left mid-upload)
  | 'not_uploaded'
  // Uploaded while the server queue was off and never processed
  | 'not_started';

type BadgeVariant = 'success' | 'warning' | 'error' | 'default' | 'info';

export interface FileItem {
  name: string;
  size: number;
  status?: FileStatus;
  error?: string;
  processingTime?: number;
  /** 0-100 while uploading */
  uploadProgress?: number;
  /** Needs attention: number of pages that failed */
  failedPages?: number;
  /** Waiting for AI capacity: local HH:MM when processing resumes */
  resumeTime?: string;
}

interface FileListProps {
  files: FileItem[];
  onRemove?: (index: number) => void;
  showStatus?: boolean;
}

const FileList: React.FC<FileListProps> = ({ files, onRemove, showStatus = false }) => {
  const getStatusBadge = (status: FileStatus): { label: string; variant: BadgeVariant } | null => {
    switch (status) {
      case 'uploaded':
        return { label: 'UPLOADED', variant: 'default' };
      case 'processing':
        return { label: 'PROCESSING', variant: 'info' };
      case 'completed':
        return { label: 'COMPLETED', variant: 'success' };
      case 'error':
        return { label: 'ERROR', variant: 'error' };
      case 'uploading':
        return { label: 'UPLOADING', variant: 'info' };
      case 'queued':
        return { label: 'QUEUED', variant: 'default' };
      case 'ready':
        return { label: 'READY', variant: 'success' };
      case 'needs_attention':
        return { label: 'NEEDS ATTENTION', variant: 'warning' };
      case 'failed':
        return { label: 'FAILED', variant: 'error' };
      case 'waiting_capacity':
        return { label: 'WAITING FOR AI CAPACITY', variant: 'warning' };
      case 'deleted':
        return { label: 'DELETED', variant: 'default' };
      case 'not_uploaded':
        return { label: 'NOT UPLOADED', variant: 'warning' };
      case 'not_started':
        return { label: 'NOT STARTED', variant: 'default' };
      default:
        return null;
    }
  };

  const getStatusDetail = (file: FileItem): { text: string; color: string } | null => {
    switch (file.status) {
      case 'completed':
        return file.processingTime
          ? { text: `Completed in ${file.processingTime.toFixed(2)}s`, color: theme.colors.success }
          : null;
      case 'error':
      case 'failed':
        return file.error ? { text: file.error, color: theme.colors.error } : null;
      case 'processing':
        return { text: 'Processing...', color: theme.colors.accent };
      case 'uploaded':
      case 'upload_pending':
        return { text: 'Waiting...', color: theme.colors.textSecondary };
      case 'uploading':
        return { text: `Uploading ${file.uploadProgress ?? 0}%`, color: theme.colors.accent };
      case 'needs_attention':
        return file.failedPages
          ? {
              text: `${file.failedPages} page${file.failedPages !== 1 ? 's' : ''} failed`,
              color: theme.colors.warning,
            }
          : null;
      case 'waiting_capacity':
        return file.resumeTime ? { text: `Resumes ${file.resumeTime}`, color: theme.colors.warning } : null;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.sm,
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {files.map((file, index) => {
        const badge = showStatus && file.status ? getStatusBadge(file.status) : null;
        const detail = showStatus && file.status ? getStatusDetail(file) : null;

        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing.sm,
              backgroundColor:
                file.status === 'processing' || file.status === 'uploading'
                  ? theme.colors.accentLight
                  : theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.radius.md,
              transition: theme.transitions.fast,
            }}
          >
            {/* File Info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                flex: 1,
                minWidth: 0,
              }}
            >
              {/* File Icon */}
              <div
                style={{
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                📄
              </div>

              {/* File Details */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: theme.typography.sizes.small,
                    fontWeight: theme.typography.weights.medium,
                    color: theme.colors.textPrimary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    marginBottom: '2px',
                  }}
                >
                  {file.name}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                  <span
                    style={{
                      fontSize: theme.typography.sizes.tiny,
                      color: theme.colors.textSecondary,
                    }}
                  >
                    {formatFileSize(file.size)}
                  </span>

                  {detail && (
                    <>
                      <span style={{ color: theme.colors.border }}>•</span>
                      <span
                        style={{
                          fontSize: theme.typography.sizes.tiny,
                          color: detail.color,
                        }}
                      >
                        {detail.text}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Status Badge or Remove Button */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.xs,
                flexShrink: 0,
              }}
            >
              {badge && <Badge label={badge.label} variant={badge.variant} size="small" />}

              {onRemove && !showStatus && (
                <button
                  onClick={() => onRemove(index)}
                  style={{
                    padding: '6px 10px',
                    fontSize: theme.typography.sizes.tiny,
                    fontWeight: theme.typography.weights.medium,
                    fontFamily: theme.typography.fontFamily,
                    backgroundColor: theme.colors.surface,
                    color: theme.colors.error,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.radius.sm,
                    cursor: 'pointer',
                    transition: theme.transitions.fast,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.error;
                    e.currentTarget.style.backgroundColor = '#FEE2E2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = theme.colors.border;
                    e.currentTarget.style.backgroundColor = theme.colors.surface;
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FileList;
